import {
  defaultEnvironmentState,
  defaultGroundCoverState,
  type EnvironmentState,
  type GroundCoverState,
  type SeasonMode,
  type SimpleSeason,
  type WeatherPreset,
} from './EnvironmentState';
import { computeSunLighting, daylightWindow, type DaylightWindow, type SunLighting } from './SunModel';
import { SEASON_ANCHORS, isFreezingSeason } from './SeasonModel';

export interface EnvironmentManagerDeps {
  /** Called whenever the sun lighting changes (time/date/latitude edits). */
  onSunChange?: (lighting: SunLighting) => void;
  /** Called with the effective day of year whenever the season/date changes. */
  onSeasonChange?: (dayOfYear: number) => void;
  /**
   * Called with the cover the ground should actually be wearing — see
   * {@link EnvironmentManager.groundCover}. Fires on a cover edit *and* on a date change, because
   * dragging out of the freezing window takes the snow off without anyone touching the switch.
   */
  onGroundCoverChange?: (cover: GroundCoverState) => void;
  /**
   * Called after any mutation of the state, including {@link EnvironmentManager.load}. The
   * environment is part of the saved document but not of the undo history, so this is what tells
   * autosave that the site changed when the time of day or the weather moved.
   */
  onChange?: (state: EnvironmentState) => void;
  /** Peak direct-sun intensity passed to the sun model. */
  maxSunIntensity?: number;
}

/**
 * Runtime owner of the canonical {@link EnvironmentState} (spec §5). Mutations recompute derived
 * lighting and notify listeners, so moving the time of day updates the sun direction/intensity — and
 * therefore the shadows — without the manager knowing anything about Three.js.
 */
export class EnvironmentManager {
  private current: EnvironmentState;

  constructor(
    private readonly deps: EnvironmentManagerDeps = {},
    initial: EnvironmentState = defaultEnvironmentState(),
  ) {
    this.current = initial;
    this.emitSun();
    this.emitSeason();
  }

  /**
   * The cover the ground is actually wearing: the stored flags, switched off outside the freezing
   * window.
   *
   * Gating here rather than in the setter is what makes the date scrub non-destructive. A site set up
   * under snow in January, dragged to July and dragged back is under snow again — the flags never
   * moved, only their effect did.
   */
  groundCover(): GroundCoverState {
    const stored = this.current.groundCover;
    if (this.isFreezing()) return { ...stored };
    return { ...stored, snow: false, ice: false };
  }

  /** Whether the date currently driving the season falls in the window where cover is offered. */
  isFreezing(): boolean {
    return isFreezingSeason(this.effectiveDayOfYear());
  }

  get state(): EnvironmentState {
    return this.current;
  }

  /** The day of year that actually drives seasonal appearance (anchor day in simple mode). */
  effectiveDayOfYear(): number {
    if (this.current.date.mode === 'calendar') return this.current.date.dayOfYear;
    const anchor = SEASON_ANCHORS.find((a) => a.season === this.current.date.season);
    return anchor?.day ?? this.current.date.dayOfYear;
  }

  /**
   * Current derived sun lighting. Driven by {@link effectiveDayOfYear} so a simple-mode season moves
   * the sun to that season's anchor day rather than leaving it on whatever calendar day was last set.
   */
  sunLighting(): SunLighting {
    return computeSunLighting(
      this.current.time,
      this.current.latitudeDegrees,
      this.effectiveDayOfYear(),
      this.deps.maxSunIntensity ?? 1,
    );
  }

  /** Sunrise/sunset for the day and latitude currently driving the sun. */
  daylight(): DaylightWindow {
    return daylightWindow(this.current.latitudeDegrees, this.effectiveDayOfYear());
  }

  setTime(hour: number, minute: number): void {
    this.current.time = {
      hour: clampInt(hour, 0, 23),
      minute: clampInt(minute, 0, 59),
    };
    this.emitSun();
    this.emitChange();
  }

  setLatitude(latitudeDegrees: number): void {
    this.current.latitudeDegrees = clamp(latitudeDegrees, -90, 90);
    this.emitSun();
    this.emitChange();
  }

  setSeasonMode(mode: SeasonMode): void {
    this.current.date.mode = mode;
    this.emitSeason();
    this.emitSun();
    this.emitChange();
  }

  setSeason(season: SimpleSeason): void {
    this.current.date.season = season;
    this.emitSeason();
    this.emitSun();
    this.emitChange();
  }

  setDayOfYear(dayOfYear: number): void {
    this.current.date.dayOfYear = clampInt(dayOfYear, 1, 365);
    this.emitSeason();
    this.emitSun();
    this.emitChange();
  }

  setWeather(weather: WeatherPreset): void {
    this.current.weather = weather;
    this.emitChange();
  }

  /**
   * Edit the winter ground cover. Partial, because the panel changes one thing at a time — a switch,
   * a variant, a slider — and none of them should have to restate the other five.
   */
  setGroundCover(changes: Partial<GroundCoverState>): void {
    const next = { ...this.current.groundCover, ...changes };
    next.snowAmount = clamp(next.snowAmount, 0, 1);
    next.iceAmount = clamp(next.iceAmount, 0, 1);
    this.current.groundCover = next;
    this.emitGroundCover();
    this.emitChange();
  }

  /** Take every cover off, whatever the date. */
  clearGroundCover(): void {
    this.setGroundCover(defaultGroundCoverState());
  }

  /** Replace the whole state (e.g. after loading a project). */
  load(state: EnvironmentState): void {
    this.current = state;
    this.emitSun();
    this.emitSeason();
    this.emitChange();
  }

  private emitSun(): void {
    this.deps.onSunChange?.(this.sunLighting());
  }

  /**
   * Season changes carry the cover with them: the same drag that moves the foliage out of winter is
   * the one that has to take the snow off the ground.
   */
  private emitSeason(): void {
    this.deps.onSeasonChange?.(this.effectiveDayOfYear());
    this.emitGroundCover();
  }

  private emitGroundCover(): void {
    this.deps.onGroundCoverChange?.(this.groundCover());
  }

  /** Not fired from the constructor: adopting an initial state is not an edit of a document. */
  private emitChange(): void {
    this.deps.onChange?.(this.current);
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function clampInt(value: number, min: number, max: number): number {
  return clamp(Math.trunc(value), min, max);
}
